import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Loader2, Edit, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { formatCurrency } from '../../lib/defaultData';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft: { bg: '#f3f4f6', color: '#6b7280' },
  sent:  { bg: '#dbeafe', color: '#1e40af' },
  paid:  { bg: '#d1fae5', color: '#065f46' },
};

function InvoiceStatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color }}
    >
      {status || 'draft'}
    </span>
  );
}

/** Invoices belonging to a single project. */
export default function ProjectInvoicesTab({ project }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/invoices`, { params: { project_id: project.id } })
      .then(res => { if (!cancelled) setInvoices(res.data?.data || []); })
      .catch(err => console.error('Failed to fetch project invoices:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project.id]);

  const handleNewInvoice = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const numRes = await axios.get(`${API}/invoices/next-number`);
      const today = new Date().toISOString().slice(0, 10);
      const due = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const payload = {
        invoice_no: numRes.data.invoice_no,
        invoice_date: today,
        due_date: due,
        // Link + prefill from the project
        project_id: project.id,
        project_name: project.project_name || '',
        project_address: project.site_address || '',
        client_id: project.client_id || '',
        client_name: project.client_name || '',
        items: [{ id: crypto.randomUUID(), description: '', amount: 0 }],
        gst_enabled: true,
        status: 'draft',
      };
      const res = await axios.post(`${API}/invoices`, payload);
      navigate(`/invoice/${res.data.id}?from_project=${project.id}`);
    } catch (err) {
      console.error('Failed to create invoice:', err);
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading...
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-16">
        <Receipt className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
        <p className="text-[#6b7280] text-lg mb-2">No invoices for this project</p>
        <p className="text-[#6b7280]/60 text-sm mb-6">Invoices created here will be linked to {project.project_name}</p>
        <Button onClick={handleNewInvoice} disabled={creating} className="bg-[#1a1a2e] text-white hover:bg-[#252545]">
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          {creating ? 'Creating...' : 'New Invoice'}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Invoices ({invoices.length})
        </h3>
        <Button
          onClick={handleNewInvoice}
          disabled={creating}
          size="sm"
          className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
        >
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          {creating ? 'Creating...' : 'New Invoice'}
        </Button>
      </div>

      <div className="space-y-1">
        <div
          className="hidden sm:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
          style={{ gridTemplateColumns: '130px 110px 1fr 90px 130px 60px' }}
        >
          <span>Invoice No</span>
          <span>Date</span>
          <span>Client</span>
          <span>Status</span>
          <span className="text-right">Subtotal</span>
          <span className="text-right">Open</span>
        </div>

        {invoices.map(inv => (
          <div
            key={inv.id}
            className="grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
            style={{
              gridTemplateColumns: '130px 110px 1fr 90px 130px 60px',
              borderBottom: '1px solid rgba(201,168,76,0.1)',
            }}
            onClick={() => navigate(`/invoice/${inv.id}?from_project=${project.id}`)}
          >
            <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{inv.invoice_no || '—'}</span>
            <span className="text-sm text-[#6b7280]">{inv.invoice_date || '—'}</span>
            <span className="text-sm truncate">{inv.client_name || '—'}</span>
            <span><InvoiceStatusBadge status={inv.status} /></span>
            <span className="text-sm font-mono text-right font-medium">
              {inv.subtotal != null ? formatCurrency(inv.subtotal) : '—'}
            </span>
            <div className="flex items-center justify-end">
              <Edit className="w-4 h-4 text-[#6b7280]" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
